<template>
    <navbar>
        <button class="navbar-toggler mobile-sidebar-toggler d-lg-none" type="button" @click="mobileSidebarToggle">
            <icon :icon="'fa fa-bars'" :color="'text-muted'"></icon>
        </button>
        <a class="navbar-brand d-none d-md-flex" href="/"></a>
        <ul class="nav navbar-nav d-none d-lg-flex mr-auto">
            <li class="nav-item">
                <a class="nav-link navbar-toggler sidebar-toggler" href="#" @click="sidebarToggle">
                    <icon :icon="'fa fa-bars'" :color="'text-muted'"></icon>
                </a>
            </li>
        </ul>
        <ul class="nav navbar-nav px-1 mr-auto d-none d-md-flex" v-if="userCanInAnyProject('projects.tasks.create')">
            <li class="nav-item">
                <router-link :to="getCreateTaskRoute()" class="btn btn-primary">{{ trans('tasks.actions.create_task') }}</router-link>
            </li>
        </ul>
        <!-- Go to task by key: Larger screens -->
        <ul class="nav navbar-nav px-1 mr-auto d-none d-lg-flex">
            <li class="nav-item">
                <form @submit.stop.prevent="goToTask()">
                    <input type="text" class="form-control header-task-search-input" :placeholder="trans('tasks.actions.go_to_task')" v-model="goToTaskInput" autocomplete="off">
                    <i class="fa fa-fw fa-external-link-alt text-muted header-task-search-icon clickable" @click="goToTask()"></i>
                </form>
            </li>
        </ul>

        <!-- Status Information buttons -->
        <ul v-if="statusInformation.length"
            class="nav navbar-nav px-1 d-none status-information-icons-width"
            :class="[statusInformation.length <= 10 ? 'd-xl-flex' : '', statusInformationProjects.length < 2 ? 'mr-auto' : '']"
            :title="trans('contacts.tabs.status_information')">
            <li class="nav-item" v-for="status in statusInformation" :key="`status-${status.key}-${getStatusInformationCount(status)}`" :title="status.label">
                <router-link :to="getTaskSearchRoute(status.title)">
                    {{ getStatusInformationCount(status) }}
                    <icon :icon="status.css_icon"
                          :color="status.css_class"></icon>
                </router-link>
            </li>
        </ul>

        <!-- Status Information drop down menu -->
        <b-navbar-nav v-if="statusInformation.length"
                      class="px-0 px-md-1 status-information-dropdown"
                      :class="[statusInformation.length <= 10 ? 'd-xl-none' : '', statusInformationProjects.length < 2 ? 'mr-auto' : '']"
                      :title="trans('contacts.tabs.status_information')">
            <b-nav-item-dropdown id="status-information-drop-down">
                <template slot="button-content">
                    <icon :icon="'fa fa-tasks'" :color="'text-muted'"></icon>
                </template>
                <div v-for="status in statusInformation" :key="`status-${status.key}-${getStatusInformationCount(status)}`">
                    <router-link :to="getTaskSearchRoute(status.title)" class="nav-link" :title="status.label">
                        <div class="dropdown-item text-truncate dropdown-item-border-bottom">
                            <icon :icon="status.css_icon"
                                  :color="status.css_class"></icon>
                            {{ getStatusInformationCount(status) }}
                            {{ status.label }}
                        </div>
                    </router-link>
                </div>
            </b-nav-item-dropdown>
        </b-navbar-nav>

        <!-- Status Information project selection -->
        <b-navbar-nav v-if="statusInformationProjects.length >= 2"
                      class="px-0 px-md-1 mr-0 mr-md-auto"
                      :title="trans('projects.actions.filter_projects')">
            <b-nav-item-dropdown id="status-information-projects-drop-down">
                <template slot="button-content">
                    <icon :icon="'far fa-list-alt'" :color="'text-muted'"></icon>
                </template>
                <b-dropdown-header tag="div" class="text-center">
                    <strong>{{ trans('projects.actions.filter_projects') }}</strong>
                </b-dropdown-header>
                <div v-for="project in statusInformationProjects" :key="`status-project-${project.secret_key}`">
                    <div class="dropdown-item text-truncate pl-2 dropdown-item-border-bottom status-information-projects-drop-down-item clickable" @click="filterStatusInformationProjects(project.secret_key)">
                        <span v-if="statusInformationProjectsFiltered.indexOf(project.secret_key) === -1" class="mt-1">
                            <icon :title="trans('projects.actions.click_to_deselect_project')" :icon="'far fa-fw fa-lg fa-check-square mr-1'" :color="'text-muted'"></icon>
                        </span>
                        <span v-else class="mt-1">
                            <icon :title="trans('projects.actions.click_to_select_project')" :icon="'far fa-fw fa-lg fa-square mr-1'" :color="'text-muted'"></icon>
                        </span>
                        {{ project.full_title }}
                    </div>
                </div>
            </b-nav-item-dropdown>
        </b-navbar-nav>

        <!-- Go to task by key: Smaller screens -->
        <ul class="navbar-nav px-1 d-lg-none">
            <li class="nav-item">
                <a href class="nav-link clickable" @click.prevent="toggleMobileGoToTaskNavbar" :title="trans('tasks.actions.go_to_task')">
                    <icon :icon="'fa fa-external-link-alt'" :color="'text-muted'"></icon>
                </a>
            </li>
        </ul>

        <!-- Service Level Agreement reminders drop down menu -->
        <b-navbar-nav v-if="slaReminders.length" class="mr-0 mr-md-2" :title="trans('sla.sla_reminders')">
            <b-nav-item-dropdown right>
                <template slot="button-content">
                    <icon :icon="'fa fa-bell'" :color="'priority-high'"></icon>
                </template>
                <b-dropdown-header tag="div" class="text-center">
                    <strong>{{ trans('sla.sla_reminders') }}</strong>
                </b-dropdown-header>
                <div v-for="slaReminder in slaReminders" :key="slaReminder.secret_key">
                    <router-link :to="`/tasks/${slaReminder.task.key}`" class="nav-link">
                        <div class="dropdown-item" :title="trans('sla.sla_started', {date: userFormattedDatetime(slaReminder.task.sla_start ? slaReminder.task.sla_start : slaReminder.task.created_at)})">
                            <icon :icon="slaReminder.task.task_priority.options.css.icon"
                                  :color="slaReminder.task.task_priority.options.css.class"
                                  :title="slaReminder.task.task_priority.title | capitalize"></icon>
                            {{ slaReminder.task.key }} ({{ diffForHumans(slaReminder.task.sla_start ? slaReminder.task.sla_start : slaReminder.task.created_at) }})
                        </div>
                    </router-link>
                </div>
            </b-nav-item-dropdown>
        </b-navbar-nav>

        <!-- Notifications drop down menu -->
        <b-navbar-nav v-if="hasPendingVacations || hasOnSiteNotifications" class="mr-0 mr-md-2" :title="trans('common.notifications')">
            <b-nav-item-dropdown right>
                <template slot="button-content">
                    <icon :icon="'fa fa-bell'" :color="'text-danger'"></icon>
                </template>
                <b-dropdown-header tag="div" class="text-center">
                    <strong>{{ trans('common.notifications') }}</strong>
                </b-dropdown-header>
                <b-dropdown-item v-if="hasPendingVacations" v-for="pending in pendingVacations" :key="pending.company" @click.prevent="goToPendingVacations(pending.company)">
                    <i class="fa fa-glass-martini text-muted"></i>
                    {{ pending.count === 1 ? trans('absences.pending_vacation_count', {number: 1}) : trans('absences.pending_vacations_count', {number: pending.count}) }}
                </b-dropdown-item>
                <b-dropdown-item v-if="hasOnSiteNotifications" v-for="onSiteNotification in onSiteNotifications" :key="onSiteNotification.secret_key" @click.prevent="goToOnSiteNotificationLinkTarget(onSiteNotification)">
                    <icon :icon="onSiteNotification.task.task_priority.options.css.icon"
                          :color="onSiteNotification.task.task_priority.options.css.class"
                          :title="`${trans('tasks.attributes.priority')}: ${onSiteNotification.task.task_priority.title}`"></icon>
                    <span v-html="onSiteNotification.message"></span>
                </b-dropdown-item>
            </b-nav-item-dropdown>
        </b-navbar-nav>

        <!-- Event (calendar) reminders drop down menu -->
        <b-navbar-nav v-if="hasReminder" class="mr-0 mr-md-2">
            <b-nav-item-dropdown id="event-reminders-drop-down" right>
                <template slot="button-content">
                    <icon :icon="'fa fa-bell'" :color="'text-warning'"></icon>
                </template>
                <b-dropdown-header tag="div" class="text-center">
                    <strong>{{ trans('calendar.event_reminders') }}</strong>
                </b-dropdown-header>
                <div v-for="reminder in eventReminders" :key="reminder.id">
                    <b-dropdown-item style="overflow-y: auto; overflow-x: hidden ; border-bottom: 0"
                                     @click.prevent="showEvent(reminder.event.secret_key)"
                                     :title="trans('calendar.actions.show_event')">
                        <div class="text-truncate">
                            {{ reminder.event.subject ? reminder.event.subject : reminder.event.type.title }}
                            <br>
                            {{ trans('calendar.attributes.start') }}: {{ reminder.formatted_event_start }}
                            <br>
                            {{ trans('calendar.attributes.end') }}: {{ reminder.formatted_event_end}}
                            <br>
                        </div>
                    </b-dropdown-item>
                    <b-dropdown-item style="border-bottom: 1px solid #cfd8dc; text-align: center;"
                                     @click.prevent="removeReminder(reminder.id)"
                                     :title="trans('calendar.actions.delete_reminder')">
                        <icon :icon="'fa fa-lg fa-trash-alt'" :color="'text-danger'"></icon>
                    </b-dropdown-item>
                </div>
            </b-nav-item-dropdown>
        </b-navbar-nav>

        <!-- Work Timer -->
        <b-navbar-nav v-if="hasWorkTimers" class="mr-0 mr-md-2 d-none d-lg-flex">
            <b-nav-item-dropdown id="work-timers-drop-down" right>
                <template slot="button-content">
                    <icon :icon="'fas fa-stopwatch'" :color="'high'"></icon>
                </template>
                <b-dropdown-header tag="div" class="text-center">
                    <strong>{{ trans('timers.headings.work_timers') }}</strong>
                </b-dropdown-header>
                <div v-for="timer in taskWorkTimers" :key="timer.id">
                    <div class="dropdown-item-border-bottom ">
                        <div class="row m-0" style="width: 280px;">
                            <span class="col-5 p-2">
                                <a :href="`/tasks/${timer.task.key}`" :title="trans('tasks.actions.go_to_task')">{{ timer.task.key }}</a>
                            </span>
                            <span
                                v-if="timer.state === 'active' && !timer.task.is_archived && userCanInProject('projects.tasks.worklog.create', timer.task.project.secret_key)"
                                class="col-5 p-2 clickable work-timer-item-hover"
                                @click="workTimerStopAction(timer)"
                                :title="trans('timers.actions.stop_timer')"
                            >
                                <i
                                    class="material-icons-outlined icons-sm icons-v-align-middle mr-1"
                                    :class="{'text-light': darkModeIsActive, 'text-dark': !darkModeIsActive}"
                                    style="margin-bottom: 4.5px;"
                                >
                                    pause_circle
                                </i>
                                {{ `${taskWorkTimerCounter}m` }}
                            </span>
                            <span
                                v-else-if="timer.state === 'paused' && !timer.task.is_archived && userCanInProject('projects.tasks.worklog.create', timer.task.project.secret_key)"
                                class="col-5 p-2 clickable work-timer-item-hover"
                                @click="workTimerResumeAction(timer)"
                                :title="trans('timers.actions.start_timer')"
                            >
                                <i
                                    class="material-icons-outlined icons-sm icons-v-align-middle mr-1"
                                    :class="{'text-light': darkModeIsActive, 'text-dark': !darkModeIsActive}"
                                    style="margin-bottom: 4.5px;"
                                >
                                    play_circle
                                </i>
                                {{ `${timer.tracked_time_in_minutes}m` }}
                            </span>
                            <span v-else class="col-5 p-2">
                                {{ trans('timers.messages.not_accessable') }}
                            </span>
                            <span
                                class="col-2 p-2 clickable work-timer-item-hover"
                                @click="workTimerCancelAction(timer)"
                                :title="trans('timers.actions.cancel_timer')"
                            >
                                <icon :icon="'far fa-lg fa-times-circle'" :color="'text-danger'"></icon>
                            </span>

                        </div>

                    </div>
                </div>
            </b-nav-item-dropdown>
        </b-navbar-nav>

        <!-- Dark mode -->
        <ul class="navbar-nav ml-auto px-1">
            <li class="nav-item">
                <a href class="nav-link clickable" @click.prevent="toggleDarkMode" :title="darkModeIsActive ? trans('common.light_mode') : trans('common.dark_mode')">
                    <icon :icon="darkModeIsActive ? 'fas fa-sun' : 'fas fa-moon'" :color="'text-dark'"></icon>
                </a>
            </li>
        </ul>

        <!-- Drop down menu for desktop view -->
        <b-navbar-nav class="nav-profile pull-right mr-lg-3 d-none d-md-flex" style="padding-right: 1rem;">
            <b-nav-item-dropdown right>
                <template slot="button-content">
                    <span class="text-dark">{{ welcomeText }}</span>
                </template>
                <b-dropdown-item class="dropdown-item-border-bottom" href="#" @click.prevent="goToProfile">
                    <i class="fa fa-user text-muted"></i>
                    {{ trans('contacts.my_profile') }}
                </b-dropdown-item>
                <b-dropdown-item class="dropdown-item-border-bottom" href="#" @click.prevent="logout">
                    <i class="fa fa-sign-out-alt text-muted"></i>
                    {{ trans('auth.log_out') }}
                </b-dropdown-item>
            </b-nav-item-dropdown>
        </b-navbar-nav>

        <!-- Drop down menu for mobile view -->
        <b-navbar-nav class="ml-0 d-md-none">
            <b-nav-item-dropdown right>
                <template slot="button-content">
                    <icon :icon="'fa fa-bars'" :color="'text-muted'"></icon>
                </template>
                <b-dropdown-header tag="div" class="text-center dropdown-item-border-bottom">
                    <strong>{{ welcomeText }}</strong>
                </b-dropdown-header>
                <b-dropdown-item class="dropdown-item-border-bottom" @click.prevent="goToCreateTask" v-if="userCanInAnyProject('projects.tasks.create')">
                    <i class="fa fa-tasks text-muted"></i>
                    {{ trans('tasks.actions.create_task') }}
                </b-dropdown-item>
                <b-dropdown-item class="dropdown-item-border-bottom" href="#" @click.prevent="goToProfile">
                    <i class="fa fa-user text-muted"></i>
                    {{ trans('contacts.my_profile') }}
                </b-dropdown-item>
                <b-dropdown-item class="dropdown-item-border-bottom" href="#" @click.prevent="logout">
                    <i class="fa fa-sign-out-alt text-muted"></i>
                    {{ trans('auth.log_out') }}
                </b-dropdown-item>
            </b-nav-item-dropdown>
        </b-navbar-nav>

        <navbar v-if="showMobileGoToTaskInput" class="d-lg-none header-task-search-navbar">
            <div class="ml-3 mr-3 w-100">
                <div class="row">
                    <div class="col">
                        <form @submit.stop.prevent="goToTask()">
                            <input type="text" class="form-control" :placeholder="trans('tasks.actions.go_to_task')" v-model="goToTaskInput" autocomplete="off">
                            <i class="fa fa-fw fa-external-link-alt text-muted header-task-search-icon-mobile clickable" @click="goToTask()"></i>
                        </form>
                    </div>
                    <div class="col-auto navbar-nav pl-3">
                        <a href class="clickable nav-link" @click.prevent="toggleMobileGoToTaskNavbar" :title="trans('common.actions.close')">
                            <icon :icon="'fa fa-times fa-lg'" :color="'text-muted'"></icon>
                        </a>
                    </div>
                </div>
            </div>
        </navbar>

        <task-work-log-modal
            :task_id="activeWorkLogTask.key"
            :show_task_key="true"
            :worklog="null"
            :remaining-work="activeWorkLogTask.work_remaining_human_readable"
            :logged-time-preset="activeWorkLogTask.logged_time"
            :complete-task-work-timer="true"
            v-if="state.showTaskWorkLogModal"
            @cancel="onTaskWorkLogModalCancelled"
            @reschedule="openTaskRescheduleModal"
            @saved="onTaskWorkLogModalSaved"
            @completetimer="workTimerCompleteAction"
        ></task-work-log-modal>

        <task-reschedule-modal
            :task_id="activeWorkLogTask.key"
            :remaining-work="activeWorkLogTask.work_remaining_human_readable"
            :time-estimated="activeWorkLogTask.time_estimation"
            :additional-time="activeWorkLogTask.additional_time"
            v-if="state.showRescheduleModal"
            @cancel="onTaskRescheduleModalCancelled"
            @saved="onTaskRescheduleModalSaved"
        ></task-reschedule-modal>

    </navbar>
</template>

<script>

    import _ from 'lodash';
    import {mapActions} from 'vuex';

    import navbar from './Navbar';
    import Icon from '@/components/Icons/Icon';

    import AbsenceController from '@/services/Absences/AbsenceController';
    import OnSiteNotificationsController from '@/services/OnSiteNotifications/OnSiteNotificationsController';
    import ReminderController from '@/services/Reminders/ReminderController';
    import SlaReminderController from '@/services/Notification/SlaReminderController';
    import TaskSearchController from '@/services/Tasks/TaskSearchController';
    import TaskWorkTimerController from '@/services/Timers/TaskWorkTimerController';
    import TaskWorkLogModal from '@/views/Tasks/Task/Partials/Show/WorkLogModal';
    import TaskRescheduleModal from "../views/Tasks/Task/Partials/Show/RescheduleModal.vue";

    export default {

        name: 'cl-header',

        components: {TaskRescheduleModal, navbar, Icon, TaskWorkLogModal},

        data() {
            return {
                hasPendingVacations: false,
                pendingVacations: [],
                hasOnSiteNotifications: false,
                onSiteNotifications: [],
                hasReminder: false,
                eventReminders: {},
                slaReminders: {},
                statusInformation: [],
                statusInformationStatuses: [],
                statusInformationProjects: [],
                statusInformationProjectsSelected: [],
                statusInformationProjectsFiltered: [],
                hasWorkTimers: false,
                taskWorkTimers: {},
                welcomeText: '',
                goToTaskInput: '',
                showMobileGoToTaskInput: false,
                reloadTimer: null,
                darkModeIsActive: false,
                taskWorkTimerCounter: 0,
                taskWorkTimerCounterId: 0,
                taskWorkTimerStarted: false,
                activeWorkLogTask: {
                    key: null,
                    work_remaining_human_readable: null,
                    time_estimation: null,
                    logged_time: null,
                    additional_time: null,
                    timer_secret_key: null,
                },
                state: {
                    showTaskWorkLogModal: false,
                    showRescheduleModal: false,
                }
            };
        },

        created() {
            this.darkModeIsActive = this.userSettingGet('dark_mode_enabled');

            if (this.darkModeIsActive) {
                document.body.classList.add('dark-mode');
            } else {
                document.body.classList.remove('dark-mode');
            }

            this.statusInformationStatuses = Array.from(this.userSettingGet('status_information_statuses') || []);
            this.statusInformationProjectsSelected =
                Array.from(this.userSettingGet('status_information_projects') || []);
            this.statusInformationProjectsFiltered =
                Array.from(this.userSettingGet('status_information_projects_filtered') || []);

            this.setWelcomeText();
            this.loadNotifications();
            this.loadTaskWorkTimers();

            const self = this;

            // check every minute
            this.reloadTimer = setInterval(function () {
                self.loadNotifications();
            }, 60000);
        },

        mounted() {
            this.$bus.$on('header.notifications.refresh', this.loadNotificationsWithStatusInfoReload);
            this.$bus.$on('timers.taskWorkTimers.refresh', this.loadTaskWorkTimers);
            this.$bus.$on('timers.generalWorkTimer.stopped', this.onGeneralWorkTimerStopped);
        },

        methods: {

            ...mapActions(['logoutAuthUser', 'swalToast', 'resetMessages', 'setMessage']),

            sidebarToggle(e) {
                e.preventDefault();
                document.body.classList.toggle('sidebar-hidden');
            },

            mobileSidebarToggle(e) {
                e.preventDefault();
                document.body.classList.toggle('sidebar-mobile-show');

                if (document.body.classList.contains('sidebar-mobile-show')) {
                    this.showOverlayMobileSidebarOpen();
                } else {
                    this.hideOverlayMobileSidebarOpen();
                }
            },

            logout() {
                if (!this.$router.currentRoute.meta.submit && this.$router.currentRoute.meta.submit !== undefined) {
                    this.$swal({
                        title: this.trans('messages.confirmation.are_you_sure'),
                        text: this.trans('messages.confirmation.logout'),
                        type: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#d33',
                        cancelButtonColor: '#3085d6',
                        confirmButtonText: this.trans('messages.confirmation.yes_log_out'),
                        cancelButtonText: this.trans('common.actions.cancel')
                    }).then((result) => {
                        if (result.value) {
                            this.$router.currentRoute.meta.submit = true;

                            this.logoutAction();
                        }
                    });
                } else {
                    this.logoutAction();
                }
            },

            logoutAction() {
                this.logoutAuthUser().then(() => {
                    this.$router.push({name: 'auth.login'});
                }).catch(error => {
                    console.log(error);
                });
            },

            setWelcomeText() {
                const firstName = this.authUser.first_name ? this.authUser.first_name : this.authUser.name;

                this.welcomeText = `${this.trans('auth.greetings.hi')} ${firstName}`;
            },

            getProfileRoute() {
                return {
                    name: 'contacts.contact.show',
                    params: {
                        contact_id: this.authUser.secret_key,
                        contact_name: this.authUser.full_name
                    }
                };
            },

            goToProfile() {
                this.$router.push(this.getProfileRoute());
            },

            getCreateTaskRoute() {
                if (this.$route.name.substr(0, 23) === 'projects.partials.show.'
                    && !!this.$route.params.project_acronym
                ) {
                    return {
                        name: 'tasks.create',
                        params: {
                            project_acronym: this.$route.params.project_acronym
                        }
                    };
                }

                return {
                    name: 'tasks.create'
                };
            },

            goToCreateTask() {
                this.$router.push(this.getCreateTaskRoute());
            },

            getTaskSearchRoute(taskStatus) {
                const self = this;
                const query = {
                    statuses: taskStatus
                };

                let infoProjects = Array.from(this.statusInformationProjectsSelected);

                infoProjects = infoProjects.filter(function (projectKey) {
                    return self.statusInformationProjectsFiltered.indexOf(projectKey) === -1;
                });

                if (infoProjects.length) {
                    query.projects = infoProjects;
                }

                if (this.$route.name === 'tasks.index') {
                    query.reload = true;
                }

                return {
                    path: '/tasks',
                    query: query
                };
            },

            goToPendingVacations(company) {
                this.$router.push({
                    name: 'contacts.contact.partials.show.pending-vacations-tab',
                    params: {
                        contact_id: company
                    }
                });
            },

            goToOnSiteNotificationLinkTarget(onSiteNotification) {
                if (onSiteNotification.appointment_request) {
                    this.$router.push({
                        name: 'tasks.show',
                        params: {
                            task_id: onSiteNotification.task.key
                        },
                        query: {
                            appointment: onSiteNotification.appointment_request.secret_key
                        }
                    });
                } else {
                    OnSiteNotificationsController.destroy(onSiteNotification.secret_key)
                        .then(() => {
                            this.loadNotifications();

                            this.$router.push({
                                name: 'tasks.show',
                                params: {
                                    task_id: onSiteNotification.task.key
                                }
                            });
                        });
                }
            },

            loadNotificationsWithStatusInfoReload() {
                this.loadNotifications(true);
            },

            loadNotifications(reloadTaskInfoSettings = false) {
                if (this.userCan('absences.approve_vacations')) {
                    AbsenceController.countPendingVacations()
                        .then(absences => {
                            this.pendingVacations = absences.data;
                            this.hasPendingVacations = this.pendingVacations.length > 0
                                && this.pendingVacations.filter(function (vacation) {
                                    return vacation.count > 0;
                                }).length > 0;
                        });
                }

                OnSiteNotificationsController.index()
                    .then(notifications => {
                        this.onSiteNotifications = notifications.data;
                        this.hasOnSiteNotifications = this.onSiteNotifications.length;
                    });

                SlaReminderController.indexOnSite()
                    .then(response => {
                        this.slaReminders = response.data;
                    });

                ReminderController.getReminders().then(reminder => {
                    this.eventReminders = reminder.data;
                    this.hasReminder = this.eventReminders.length > 0;
                });

                if (reloadTaskInfoSettings) {
                    this.statusInformationStatuses =
                        Array.from(this.userSettingGet('status_information_statuses') || []);
                    this.statusInformationProjectsSelected =
                        Array.from(this.userSettingGet('status_information_projects') || []);
                }

                const infoStatuses = Array.from(this.statusInformationStatuses);
                const infoProjects = Array.from(this.statusInformationProjectsSelected);

                if (infoStatuses.length) {
                    const params = {
                        statuses: infoStatuses,
                        projects: infoProjects
                    };

                    if (this.authUser && this.authUser.secret_key) {
                        TaskSearchController.getStatusCountsForUser(this.authUser.secret_key, params).then(response => {
                            this.statusInformation = response.data.statusInformation;
                            this.statusInformationProjects = response.data.statusInformationProjects;
                        });
                    }
                } else {
                    this.statusInformation = [];
                    this.statusInformationProjects = [];
                }
            },

            loadTaskWorkTimers() {
                this.taskWorkTimers = {};
                this.hasWorkTimers = false;

                this.stopTaskWorkTimeCounter(true);

                TaskWorkTimerController.index()
                    .then(response => {
                        this.taskWorkTimers = response.data;
                        this.hasWorkTimers = this.taskWorkTimers.length > 0;

                        let activeTimer = _.find(this.taskWorkTimers, {state: 'active'});

                        if (activeTimer) {
                            this.taskWorkTimerCounter = activeTimer.tracked_time_in_minutes;
                            this.startTaskWorkTimeCounter();
                        }
                    });
            },

            showEvent(event) {
                this.$router.push({
                    name: 'calendar.index',
                    query: {event: event}
                });
            },

            removeReminder(reminderId) {
                ReminderController.destroy(reminderId).then(() => {
                    this.loadNotifications();
                    this.$bus.$emit('header.notifications.refresh');
                });
            },

            filterStatusInformationProjects(projectKey) {
                const index = this.statusInformationProjectsFiltered.indexOf(projectKey);

                if (index === -1) {
                    this.statusInformationProjectsFiltered.push(projectKey);
                } else {
                    this.statusInformationProjectsFiltered.splice(index, 1);
                }

                this.userSettingSet('status_information_projects_filtered', this.statusInformationProjectsFiltered);
            },

            getStatusInformationCount(statusData) {
                const infoProjects = this.statusInformationProjectsSelected;
                let statusCounts = [];

                if (infoProjects.length >= 2) {
                    const self = this;

                    statusCounts = statusData.counts.filter(function (statusCount) {
                        return self.statusInformationProjectsFiltered.indexOf(statusCount.project) === -1;
                    });
                } else {
                    statusCounts = statusData.counts;
                }

                return statusCounts.reduce(function (count, status) {
                    count += parseInt(status.count);
                    return count;
                }, 0);
            },

            toggleMobileGoToTaskNavbar() {
                this.showMobileGoToTaskInput = !this.showMobileGoToTaskInput;

                const main = document.getElementsByClassName('main');
                const sidebar = document.getElementsByClassName('sidebar');

                main[0].classList.toggle('task-search-navbar-active');
                sidebar[0].classList.toggle('task-search-navbar-active');
            },

            goToTask() {
                if (this.goToTaskInput.trim() === '') {
                    this.swalToast({
                        type: 'warning',
                        message: 'Please enter a task key, e.g. "ABC-123".',
                        timer: 5000
                    });
                    return;
                }

                this.documentShowWait();

                const taskKey = this.goToTaskInput.trim().toUpperCase();

                const params = {
                    task: taskKey
                };

                TaskSearchController.taskIsAccessibleByUser(params).then(response => {
                    this.documentHideWait();

                    if (response.data.accessible) {
                        this.$router.push({
                            name: 'tasks.show',
                            params: {
                                task_id: taskKey
                            }
                        });
                    } else {
                        this.swalToast({
                            type: 'warning',
                            message: this.trans('messages.error.tasks.not_found_or_unauthorized'),
                            timer: 5000
                        });
                    }
                });
            },

            toggleDarkMode() {
                document.body.classList.toggle('dark-mode');
                this.darkModeIsActive = !this.darkModeIsActive;

                this.userSettingSet('dark_mode_enabled', this.darkModeIsActive);
            },

            workTimerStopAction(timer) {
                this.stopTaskWorkTimeCounter();

                TaskWorkTimerController.stop(timer.secret_key)
                    .then(response => {
                        const type = response.data.success ? 'success' : 'error';
                        const message = response.data.error ? response.data.error : response.data.message;

                        if (response.data.timer) {
                            this.$bus.$emit('timers.taskWorkTimers.refresh');
                            this.$bus.$emit('task.taskWorkTimer.refresh');

                            this.showLogWorkModal(
                                response.data.timer,
                                response.data.authuser_hourly_rate_external,
                                response.data.authuser_project_supplier
                            );
                        } else {
                            this.swalToast({type: type, message: message});
                        }
                    });
            },

            workTimerResumeAction(timer) {
                TaskWorkTimerController.resume(timer.secret_key)
                    .then(response => {
                        const type = response.data.success ? 'success' : 'error';
                        const message = response.data.error ? response.data.error : response.data.message;

                        this.swalToast({type: type, message: message});
                        this.$bus.$emit('timers.taskWorkTimers.refresh');
                        this.$bus.$emit('task.taskWorkTimer.refresh');
                        this.$bus.$emit('timers.generalWorkTimer.start');
                    });
            },

            workTimerCompleteAction() {
                TaskWorkTimerController.complete(this.activeWorkLogTask.timer_secret_key)
                    .then(response => {
                        const type = response.data.success ? 'success' : 'error';
                        const message = response.data.error ? response.data.error : response.data.message;

                        if (response.data.success) {
                            this.$bus.$emit('timers.taskWorkTimers.refresh');
                            this.$bus.$emit('task.taskWorkTimer.refresh');
                        }

                        this.swalToast({type: type, message: message});
                    });
            },

            workTimerCancelAction(timer) {
                TaskWorkTimerController.cancel(timer.secret_key)
                    .then(response => {
                        const type = response.data.success ? 'success' : 'error';
                        const message = response.data.error ? response.data.error : response.data.message;

                        this.swalToast({type: type, message: message});
                        this.$bus.$emit('timers.taskWorkTimers.refresh');
                        this.$bus.$emit('task.taskWorkTimer.refresh');
                    });
            },

            checkForHourlyRates(task, currentUserHourlyRateExternal, currentUsersProjectSupplier) {
                let hasHourlyRates = false;

                if (!this.userCanInProject('projects.tasks.worklog.create', task.project.secret_key)) {
                    return hasHourlyRates;
                }

                if (this.authUser.company_id && this.authUser.company_id === task.project.owner_id) {
                    hasHourlyRates = !!task.project.default_hourly_rate
                        || !!currentUserHourlyRateExternal
                        || !!this.userSettingGet('employee.hourly_rate_external');
                } else if (!!currentUsersProjectSupplier) {
                    hasHourlyRates = !!task.project.default_hourly_rate
                        && (!!currentUsersProjectSupplier.hourly_rate_external
                            || !!currentUserHourlyRateExternal
                            || !!this.userSettingGet('employee.hourly_rate_external'));
                }

                return hasHourlyRates;
            },

            showLogWorkModal(timer, currentUserHourlyRateExternal, currentUsersProjectSupplier) {
                if (this.checkForHourlyRates(timer.task, currentUserHourlyRateExternal, currentUsersProjectSupplier)) {
                    this.state.showTaskWorkLogModal = true;

                    this.activeWorkLogTask.key = timer.task.key;
                    this.activeWorkLogTask.work_remaining_human_readable = timer.task.work_remaining_human_readable;
                    this.activeWorkLogTask.time_estimation = timer.task.time_estimation;
                    this.activeWorkLogTask.logged_time = `${timer.tracked_time_in_minutes}m`;
                    this.activeWorkLogTask.additional_time = null;
                    this.activeWorkLogTask.timer_secret_key = timer.secret_key;
                } else {
                    this.showHourlyRatesRequiredWarning();
                }
            },

            showHourlyRatesRequiredWarning() {
                this.swalToast({
                    type: 'warning',
                    message: this.trans('messages.warning.tasks.hourly_rates_required'),
                    timer: 30000
                });
            },

            onTaskWorkLogModalSaved() {
                this.$bus.$emit('task.refresh');
                this.$bus.$emit('tasks.work-logs.refresh');
                this.$bus.$emit('tasks.taskLogs.refresh');

                this.state.showTaskWorkLogModal = false;
                this.resetActiveWorkLogTask();
            },

            onTaskWorkLogModalCancelled() {
                this.state.showTaskWorkLogModal = false;
                this.resetActiveWorkLogTask();
            },

            openTaskRescheduleModal(additionalTime) {
                this.state.showTaskWorkLogModal = false;

                this.activeWorkLogTask.additional_time = additionalTime;
                this.state.showRescheduleModal = true;
            },

            onTaskRescheduleModalCancelled() {
                this.state.showRescheduleModal = false;
                this.resetActiveWorkLogTask();
            },

            onTaskRescheduleModalSaved() {
                this.$bus.$emit('task.refresh');
                this.$bus.$emit('tasks.work-logs.refresh');
                this.$bus.$emit('tasks.taskLogs.refresh');

                this.state.showRescheduleModal = false;
                this.resetActiveWorkLogTask();
            },

            resetActiveWorkLogTask() {
                this.activeWorkLogTask.key = null;
                this.activeWorkLogTask.work_remaining_human_readable = null;
                this.activeWorkLogTask.time_estimation = null;
                this.activeWorkLogTask.logged_time = null;
                this.activeWorkLogTask.additional_time = null;
                this.activeWorkLogTask.timer_secret_key = null;
            },

            startTaskWorkTimeCounter() {
                if (this.taskWorkTimerStarted) {
                    return;
                }

                this.taskWorkTimerCounterId += 1;
                this.taskWorkTimerStarted = true;
                this.updateTaskWorkTimeCounter();
            },

            updateTaskWorkTimeCounter() {
                const id = this.taskWorkTimerCounterId;

                setTimeout(() => {
                    if (this.taskWorkTimerStarted && this.taskWorkTimerCounterId === id) {
                        this.taskWorkTimerCounter += 1;

                        this.updateTaskWorkTimeCounter();
                    }
                }, 60000);
            },

            stopTaskWorkTimeCounter(resetCounter = false) {
                this.taskWorkTimerStarted = false;
                if (resetCounter) {
                    this.taskWorkTimerCounter = 0;
                }
            },

            onGeneralWorkTimerStopped() {
                let timer = _.find(this.taskWorkTimers, {state: 'active'});

                if (timer) {
                    this.workTimerStopAction(timer);
                }
            },

        },

        watch: {
            '$route.name': function () {
                // Hide mobile sidebar if open
                if (document.body.classList.contains('sidebar-mobile-show')) {
                    document.body.classList.remove('sidebar-mobile-show');
                    this.hideOverlayMobileSidebarOpen();
                }
            },

            '$route.params': function () {
                // Hide mobile sidebar if open
                if (document.body.classList.contains('sidebar-mobile-show')) {
                    document.body.classList.remove('sidebar-mobile-show');
                    this.hideOverlayMobileSidebarOpen();
                }
            }
        },

        beforeDestroy: function () {
            clearInterval(this.reloadTimer);
        }
    };

</script>



// WEBPACK FOOTER //
// src/components/Header.vue